import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
    Background,
    Controls,
    Handle,
    MarkerType,
    Position,
    ReactFlowProvider,
    useEdgesState,
    useNodesState,
} from 'reactflow';
import 'reactflow/dist/style.css';
import Iconify from 'src/components/Iconify';
import { Badge } from 'src/@/components/ui/badge';
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from 'src/@/components/ui/popover';
import { ScrollArea } from 'src/@/components/ui/scroll-area';
import PerguntasNodeDrawer from './PerguntasNodeDrawer';
import { TIPOS_IMOVEL, buildInitialWorkflow } from './tiposImovel';

function BaseNode({ data, selected, variant }) {
    const perguntas = data?.perguntas || [];
    const totalAtivas = perguntas.filter((p) => p.ativa).length;
    const totalQualif = perguntas.filter((p) => p.qualificatoria).length;
    const hasLabel = !!(data?.label || '').trim();

    const styles = {
        root: 'bg-gradient-to-br from-[#7C3AED] to-[#6d28d9] text-white border-0 shadow-lg',
        finalidade: 'bg-white border-2 border-violet-300 shadow-sm',
        destino: 'bg-white border-2 border-blue-200 shadow-sm',
        tipo: 'bg-white border-2 border-gray-200 shadow-sm hover:border-[#7C3AED]/40 cursor-pointer',
    };
    const iconBg = {
        root: 'bg-white/20',
        finalidade: 'bg-violet-50',
        destino: 'bg-blue-50',
        tipo: 'bg-gray-50',
    };
    const iconColor = {
        root: 'text-white',
        finalidade: 'text-violet-600',
        destino: 'text-blue-600',
        tipo: 'text-gray-600',
    };
    const minW = variant === 'tipo' ? 'min-w-[160px]' : 'min-w-[180px]';
    const isTipo = variant === 'tipo';

    return (
        <div
            className={`group relative px-3 py-2.5 rounded-2xl ${styles[variant]} ${minW} transition-all ${
                selected ? 'ring-2 ring-offset-2 ring-[#7C3AED]' : ''
            }`}
        >
            {variant !== 'root' && (
                <Handle type="target" position={Position.Top} className="!w-1 !h-1 !opacity-0 !border-0" />
            )}
            {isTipo && data?.onRequestDelete && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        data.onRequestDelete();
                    }}
                    className="nodrag absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remover do fluxo"
                >
                    <Iconify icon="eva:close-outline" width={14} />
                </button>
            )}
            <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${iconBg[variant]}`}>
                    <Iconify icon={data?.icon || 'mdi:folder-outline'} width={16} className={iconColor[variant]} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-sm font-semibold truncate ${variant === 'root' ? 'text-white' : 'text-gray-900'}`}>
                        {hasLabel ? data.label : 'Sem nome'}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                        {totalAtivas > 0 ? (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-auto bg-violet-50 text-violet-700 border-violet-200">
                                {totalAtivas} ess.
                            </Badge>
                        ) : (
                            <span className={`text-[10px] ${variant === 'root' ? 'text-white/70' : 'text-gray-400'}`}>
                                {isTipo ? 'clique para configurar' : 'sem perguntas'}
                            </span>
                        )}
                        {totalQualif > 0 && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-auto bg-blue-50 text-blue-700 border-blue-200">
                                {totalQualif} qualif.
                            </Badge>
                        )}
                    </div>
                </div>
            </div>
            <Handle type="source" position={Position.Bottom} className="!w-1 !h-1 !opacity-0 !border-0" />
        </div>
    );
}

const RootNode = (props) => <BaseNode {...props} variant="root" />;
const FinalidadeNode = (props) => <BaseNode {...props} variant="finalidade" />;
const DestinoNode = (props) => <BaseNode {...props} variant="destino" />;
const TipoNode = (props) => <BaseNode {...props} variant="tipo" />;

const nodeTypes = {
    rootNode: RootNode,
    finalidadeNode: FinalidadeNode,
    destinoNode: DestinoNode,
    tipoNode: TipoNode,
};

const edgeDefaults = {
    type: 'smoothstep',
    animated: false,
    selectable: false,
    deletable: false,
    focusable: false,
    style: { stroke: '#c4b5fd', strokeWidth: 2 },
    markerEnd: { type: MarkerType.ArrowClosed, color: '#c4b5fd' },
};

const TIPO_SPACING = 190;
const TIPO_Y_OFFSET = 170;

function AddTipoPopover({ anchor, onPick }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return TIPOS_IMOVEL;
        return TIPOS_IMOVEL.filter((t) => t.label.toLowerCase().includes(q));
    }, [query]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>{anchor}</PopoverTrigger>
            <PopoverContent align="start" className="w-72 p-0">
                <div className="p-3 border-b">
                    <Input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Buscar tipo de imóvel"
                        className="rounded-lg border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 h-9"
                    />
                </div>
                <ScrollArea className="h-64">
                    <div className="p-2 flex flex-col gap-0.5">
                        {filtered.length === 0 ? (
                            <p className="text-xs text-gray-400 text-center py-6">Nenhum tipo encontrado</p>
                        ) : (
                            filtered.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => {
                                        onPick(t);
                                        setOpen(false);
                                        setQuery('');
                                    }}
                                    className="flex items-center gap-2 text-left px-2 py-1.5 rounded-lg hover:bg-violet-50 transition-colors"
                                >
                                    <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                                        <Iconify icon={t.icon} width={14} className="text-gray-600" />
                                    </div>
                                    <span className="text-sm text-gray-700 truncate">{t.label}</span>
                                </button>
                            ))
                        )}
                    </div>
                </ScrollArea>
            </PopoverContent>
        </Popover>
    );
}

const PROTECTED_TYPES = new Set(['rootNode', 'finalidadeNode', 'destinoNode']);

function InnerCanvas({ workflow, onChange, getEssencialDisabled, getQualifDisabled }) {
    const initialSnapshot = useMemo(() => {
        if (workflow?.nodes?.length) return { nodes: workflow.nodes, edges: workflow.edges || [] };
        return buildInitialWorkflow();
    }, []);

    const [rawNodes, setNodes, onNodesChange] = useNodesState(initialSnapshot.nodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(initialSnapshot.edges);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const skipFirstSync = useRef(false);

    const deleteNode = useCallback(
        (id) => {
            if (!id) return;
            const target = rawNodes.find((n) => n.id === id);
            if (!target || PROTECTED_TYPES.has(target.type)) return;
            const descendants = new Set();
            const collect = (nodeId) => {
                descendants.add(nodeId);
                edges.filter((e) => e.source === nodeId).forEach((e) => collect(e.target));
            };
            collect(id);
            setNodes((nds) => nds.filter((n) => !descendants.has(n.id)));
            setEdges((eds) => eds.filter((e) => !descendants.has(e.source) && !descendants.has(e.target)));
            setDrawerOpen(false);
            setSelectedNodeId(null);
        },
        [rawNodes, edges, setNodes, setEdges]
    );

    const nodes = useMemo(
        () =>
            rawNodes.map((n) => {
                if (n.type === 'tipoNode') {
                    return {
                        ...n,
                        deletable: true,
                        data: { ...n.data, onRequestDelete: () => deleteNode(n.id) },
                    };
                }
                return { ...n, deletable: false, draggable: n.type !== 'rootNode' };
            }),
        [rawNodes, deleteNode]
    );

    useEffect(() => {
        if (!workflow?.nodes?.length && !skipFirstSync.current) {
            skipFirstSync.current = true;
            onChange(initialSnapshot);
            return;
        }
        const clean = rawNodes.map((n) => ({
            ...n,
            data: Object.fromEntries(Object.entries(n.data || {}).filter(([k]) => k !== 'onRequestDelete')),
        }));
        onChange({ nodes: clean, edges });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawNodes, edges]);

    const selectedNode = useMemo(
        () => rawNodes.find((n) => n.id === selectedNodeId) || null,
        [rawNodes, selectedNodeId]
    );

    const fixedEdges = useMemo(
        () => edges.map((e) => ({ ...e, ...edgeDefaults })),
        [edges]
    );

    const onNodeClick = useCallback((_, node) => {
        setSelectedNodeId(node.id);
        if (node.type === 'tipoNode') {
            setDrawerOpen(true);
        }
    }, []);

    const onNodesDelete = useCallback(
        (deleted) => {
            const ids = deleted
                .filter((n) => !PROTECTED_TYPES.has(n.type))
                .map((n) => n.id);
            if (!ids.length) return;
            const descendants = new Set();
            const collect = (nodeId) => {
                descendants.add(nodeId);
                edges.filter((e) => e.source === nodeId).forEach((e) => collect(e.target));
            };
            ids.forEach(collect);
            setNodes((nds) => nds.filter((n) => !descendants.has(n.id)));
            setEdges((eds) => eds.filter((e) => !descendants.has(e.source) && !descendants.has(e.target)));
        },
        [edges, setNodes, setEdges]
    );

    const addTipoChild = useCallback(
        (parentId, tipo) => {
            const parent = rawNodes.find((n) => n.id === parentId);
            if (!parent) return;
            const existingChildrenIds = edges
                .filter((e) => e.source === parentId)
                .map((e) => e.target);
            const newId = `tipo_${tipo.id}_${Date.now()}`;
            const allChildrenIds = [...existingChildrenIds, newId];
            const totalWidth = (allChildrenIds.length - 1) * TIPO_SPACING;
            const startX = parent.position.x - totalWidth / 2;
            const childY = parent.position.y + TIPO_Y_OFFSET;

            const newNode = {
                id: newId,
                type: 'tipoNode',
                position: { x: 0, y: childY },
                data: { label: tipo.label, icon: tipo.icon, tipoId: tipo.id, perguntas: [] },
            };
            const newEdge = { id: `e_${parentId}_${newId}`, source: parentId, target: newId, ...edgeDefaults };

            setNodes((nds) => {
                const withNew = [...nds, newNode];
                return withNew.map((n) => {
                    const idx = allChildrenIds.indexOf(n.id);
                    if (idx === -1) return n;
                    return { ...n, position: { x: startX + idx * TIPO_SPACING, y: childY } };
                });
            });
            setEdges((eds) => [...eds, newEdge]);
        },
        [rawNodes, edges, setNodes, setEdges]
    );

    const updateNodeData = useCallback(
        (id, patch) => {
            setNodes((nds) => nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)));
        },
        [setNodes]
    );

    const resetWorkflow = useCallback(() => {
        const fresh = buildInitialWorkflow();
        setNodes(fresh.nodes);
        setEdges(fresh.edges);
        setDrawerOpen(false);
        setSelectedNodeId(null);
    }, [setNodes, setEdges]);

    const activeParentId = selectedNodeId && selectedNode?.type !== 'tipoNode' ? selectedNodeId : null;
    const targetLabel = activeParentId ? selectedNode?.data?.label : null;

    return (
        <>
            <div className="relative h-[600px] rounded-xl border border-gray-200 bg-gray-50/50 overflow-hidden">
                <div className="absolute top-3 left-3 z-10 flex flex-wrap gap-2">
                    <AddTipoPopover
                        anchor={
                            <Button
                                type="button"
                                size="sm"
                                disabled={!activeParentId}
                                className="rounded-xl bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm disabled:opacity-60"
                            >
                                <Iconify icon="mdi:home-plus-outline" width={14} className="mr-1" />
                                Adicionar tipo de imóvel
                            </Button>
                        }
                        onPick={(tipo) => activeParentId && addTipoChild(activeParentId, tipo)}
                    />

                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={resetWorkflow}
                        title="Restaurar estrutura padrão"
                        className="rounded-xl bg-white text-gray-500 hover:text-gray-700 border-gray-200 shadow-sm"
                    >
                        <Iconify icon="mdi:restore" width={14} />
                    </Button>
                </div>

                <div className="absolute top-3 right-3 z-10 bg-white/90 backdrop-blur rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] text-gray-500 shadow-sm max-w-xs">
                    {activeParentId ? (
                        <span>Tipos serão adicionados em <strong className="text-gray-700">{targetLabel}</strong></span>
                    ) : (
                        <span>Selecione uma categoria (Comercial, Residencial…) para adicionar tipos</span>
                    )}
                </div>

                <ReactFlow
                    nodes={nodes}
                    edges={fixedEdges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={onNodeClick}
                    onNodesDelete={onNodesDelete}
                    onPaneClick={() => setSelectedNodeId(null)}
                    nodeTypes={nodeTypes}
                    defaultEdgeOptions={edgeDefaults}
                    fitView
                    fitViewOptions={{ padding: 0.2 }}
                    minZoom={0.2}
                    maxZoom={1.5}
                    deleteKeyCode={['Delete', 'Backspace']}
                    nodesConnectable={false}
                    edgesFocusable={false}
                    edgesUpdatable={false}
                >
                    <Background color="#e5e7eb" gap={16} />
                    <Controls className="!shadow-md !border !border-gray-200 !rounded-xl overflow-hidden" />
                </ReactFlow>
            </div>

            <PerguntasNodeDrawer
                open={drawerOpen}
                onOpenChange={(next) => {
                    setDrawerOpen(next);
                    if (!next) setSelectedNodeId(null);
                }}
                node={selectedNode}
                onChangeLabel={(label) => updateNodeData(selectedNodeId, { label })}
                onChangePerguntas={(perguntas) => updateNodeData(selectedNodeId, { perguntas })}
                onDeleteNode={() => deleteNode(selectedNodeId)}
                getEssencialDisabled={getEssencialDisabled}
                getQualifDisabled={getQualifDisabled}
            />
        </>
    );
}

export default function PerguntasFlowCanvas(props) {
    return (
        <ReactFlowProvider>
            <InnerCanvas {...props} />
        </ReactFlowProvider>
    );
}
